import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  PlayArrow, 
  Pause, 
  Stop, 
  VolumeUp, 
  Speed, 
  Person, 
  Person2,
  Refresh,
  Download,
  Settings,
  Mic,
  MicOff,
  SkipNext,
  SkipPrevious,
  VolumeDown,
  VolumeMute
} from '@mui/icons-material';
import { useTheme } from '../utils/theme-context';
import apiService from '../services/api';

const TTSTest = () => {
  const { theme } = useTheme();
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentText, setCurrentText] = useState('');
  const [selectedVoice, setSelectedVoice] = useState('en-IN');
  const [speed, setSpeed] = useState(1.0);
  const [volume, setVolume] = useState(0.8);
  const [audioUrl, setAudioUrl] = useState(null);
  const [error, setError] = useState('');
  const [showVoicePanel, setShowVoicePanel] = useState(true);
  const [availableVoices, setAvailableVoices] = useState([]);
  const [healthStatus, setHealthStatus] = useState(null);
  
  const audioRef = useRef(null);
  const progressRef = useRef(null);

  // Test content
  const testContent = `📋 Summary
The chapter 'A Bottle of Dew' revolves around the story of Rama Natha, a man who believed in a magic potion that could turn any object into gold. After meeting a sage who revealed the secret to creating the potion using dew collected from banana plants, Rama Natha embarked on a journey to collect the necessary dew to transform his life.

⭐ Key Points
• Rama Natha's belief in a magic potion that could turn objects into gold
• The sage's guidance on collecting dew from banana plants to create the magic potion
• Rama Natha's transformation from a careless individual to a hardworking man focused on collecting dew

📖 Detailed Content
The chapter 'A Bottle of Dew' introduces us to Rama Natha, the son of a wealthy landlord who was more interested in finding a magic potion to turn objects into gold than managing his inherited lands. His wife, Madhumati, grew concerned about their financial situation due to Rama Natha's obsession. However, everything changed when the sage Mahipati visited their town and shared the secret of creating the magic potion using dew collected from banana plants.

Rama Natha, initially skeptical but desperate for wealth, followed the sage's advice and diligently planted banana plants in his fields. With the help of his wife, he tended to the plants and collected dew during the winter months. Their joint effort not only led to a successful banana crop but also brought financial stability to their lives.

The story emphasizes the transformation of Rama Natha from a dreamer to a hardworking individual willing to put in the effort required for success. It highlights the importance of perseverance, dedication, and cooperation in achieving one's goals. Through Rama Natha's journey, readers learn the value of hard work and the rewards it can bring.`;

  useEffect(() => {
    setCurrentText(testContent);
    fetchHealthStatus();
    fetchAvailableVoices();
  }, []);

  const fetchHealthStatus = async () => {
    try {
      const response = await apiService.getTTSHealth();
      setHealthStatus(response);
    } catch (error) {
      console.error('Health check failed:', error);
      setHealthStatus({ status: 'error', message: 'TTS service unavailable' });
    }
  };

  const fetchAvailableVoices = async () => {
    try {
      const response = await apiService.getTTSVoices();
      setAvailableVoices(response.voices || []);
    } catch (error) {
      console.error('Failed to fetch voices:', error);
    }
  };

  const generateTTS = async (text, voice, speed) => {
    setIsLoading(true);
    setError('');
    
    try {
      const response = await apiService.streamTTSAudio(text, voice, speed);
      
      // Create blob URL for audio
      const blob = new Blob([response], { type: 'audio/mpeg' });
      const url = URL.createObjectURL(blob);
      setAudioUrl(url);
      
      return url;
    } catch (error) {
      console.error('TTS generation failed:', error);
      setError('Failed to generate audio. Please try again.');
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const handlePlay = async () => {
    if (!currentText.trim()) {
      setError('Please enter some text to convert to speech.');
      return;
    }

    if (audioRef.current && audioUrl) {
      // Resume existing audio
      audioRef.current.play();
      setIsPlaying(true);
    } else {
      // Generate new audio
      const url = await generateTTS(currentText, selectedVoice, speed);
      if (url) {
        audioRef.current.src = url;
        audioRef.current.volume = volume;
        audioRef.current.play();
        setIsPlaying(true);
      }
    }
  };

  const handlePause = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  };

  const handleStop = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlaying(false);
    }
  };

  const handleSpeedChange = (newSpeed) => {
    setSpeed(newSpeed);
    if (audioRef.current) {
      audioRef.current.playbackRate = newSpeed;
    }
  };

  const handleVolumeChange = (newVolume) => {
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
  };

  const handleVoiceChange = (voice) => {
    setSelectedVoice(voice);
    // Stop current audio when voice changes
    if (audioRef.current) {
      handleStop();
      setAudioUrl(null);
    }
  };

  const handleDownload = async () => {
    if (!currentText.trim()) {
      setError('Please enter some text to convert to speech.');
      return;
    }

    setIsLoading(true);
    try {
      const response = await apiService.streamTTSAudio(currentText, selectedVoice, speed);
      
      const blob = new Blob([response], { type: 'audio/mpeg' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'tts_audio.mp3';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
      setError('Failed to download audio.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTestTTS = async () => {
    setIsLoading(true);
    setError('');
    
    try {
      const response = await apiService.testTTS();
      
      const blob = new Blob([response], { type: 'audio/mpeg' });
      const url = URL.createObjectURL(blob);
      setAudioUrl(url);
      
      audioRef.current.src = url;
      audioRef.current.volume = volume;
      audioRef.current.play();
      setIsPlaying(true);
    } catch (error) {
      console.error('Test TTS failed:', error);
      setError('Test audio generation failed.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAudioEnded = () => {
    setIsPlaying(false);
  };

  const handleAudioTimeUpdate = () => {
    if (audioRef.current && progressRef.current) {
      const progress = (audioRef.current.currentTime / audioRef.current.duration) * 100;
      progressRef.current.style.width = `${progress}%`;
    }
  };

  const handleSeek = (e) => {
    if (audioRef.current) {
      const rect = e.currentTarget.getBoundingClientRect();
      const percent = (e.clientX - rect.left) / rect.width;
      audioRef.current.currentTime = percent * audioRef.current.duration;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800">
      <div className="container mx-auto px-4 py-6">
        <div className="max-w-6xl mx-auto">
          
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white/60 dark:bg-gray-900/60 backdrop-blur-lg rounded-2xl shadow-lg p-6 mb-6"
          >
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-200 mb-2">
                  🎤 TTS Test Page
                </h1>
                <p className="text-gray-600 dark:text-gray-400">
                  Test the Text-to-Speech functionality with beautiful voice controls
                </p>
              </div>
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setShowVoicePanel(!showVoicePanel)}
                  className="p-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl hover:from-purple-600 hover:to-pink-600 transition-all duration-200"
                >
                  <Settings className="w-5 h-5" />
                </button>
                {healthStatus && (
                  <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                    healthStatus.status === 'healthy' 
                      ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                      : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                  }`}>
                    {healthStatus.status === 'healthy' ? '🟢 Online' : '🔴 Offline'}
                  </div>
                )}
              </div>
            </div>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            
            {/* Content Panel */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              className="lg:col-span-2"
            >
              <div className="bg-white/60 dark:bg-gray-900/60 backdrop-blur-lg rounded-2xl shadow-lg p-6">
                <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-4">
                  📝 Test Content
                </h2>
                <textarea
                  value={currentText}
                  onChange={(e) => setCurrentText(e.target.value)}
                  className="w-full h-96 p-4 bg-white/50 dark:bg-gray-800/50 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-800 dark:text-gray-200 resize-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200"
                  placeholder="Enter text to convert to speech..."
                />
                
                {error && (
                  <div className="mt-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg text-red-800 dark:text-red-300">
                    {error}
                  </div>
                )}
              </div>
            </motion.div>

            {/* Voice Control Panel */}
            <AnimatePresence>
              {showVoicePanel && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ delay: 0.2 }}
                  className="lg:col-span-1"
                >
                  <div className="bg-white/60 dark:bg-gray-900/60 backdrop-blur-lg rounded-2xl shadow-lg p-6 sticky top-6">
                    <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-6 flex items-center gap-2">
                      <Mic className="text-purple-500" />
                      Voice Controls
                    </h2>

                    {/* Voice Selection */}
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Voice Selection
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        {availableVoices.map((voice) => (
                          <button
                            key={voice.name}
                            onClick={() => handleVoiceChange(voice.name)}
                            className={`p-3 rounded-lg border-2 transition-all duration-200 ${
                              selectedVoice === voice.name
                                ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300'
                                : 'border-gray-200 dark:border-gray-700 hover:border-purple-300 dark:hover:border-purple-600'
                            }`}
                          >
                            <div className="text-sm font-medium">{voice.name}</div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {voice.description}
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Speed Control */}
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
                        <Speed className="w-4 h-4" />
                        Speed: {speed}x
                      </label>
                      <input
                        type="range"
                        min="0.5"
                        max="2.0"
                        step="0.1"
                        value={speed}
                        onChange={(e) => handleSpeedChange(parseFloat(e.target.value))}
                        className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
                      />
                      <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
                        <span>0.5x</span>
                        <span>1.0x</span>
                        <span>2.0x</span>
                      </div>
                    </div>

                    {/* Volume Control */}
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
                        <VolumeUp className="w-4 h-4" />
                        Volume: {Math.round(volume * 100)}%
                      </label>
                      <div className="flex items-center gap-2">
                        <VolumeMute className="w-4 h-4 text-gray-400" />
                        <input
                          type="range"
                          min="0"
                          max="1"
                          step="0.1"
                          value={volume}
                          onChange={(e) => handleVolumeChange(parseFloat(e.target.value))}
                          className="flex-1 h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
                        />
                        <VolumeUp className="w-4 h-4 text-gray-400" />
                      </div>
                    </div>

                    {/* Playback Controls */}
                    <div className="mb-6">
                      <div className="flex items-center justify-center gap-3 mb-4">
                        <button
                          onClick={handleStop}
                          className="p-3 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-all duration-200"
                          title="Stop"
                        >
                          <Stop className="w-5 h-5" />
                        </button>
                        <button
                          onClick={isPlaying ? handlePause : handlePlay}
                          disabled={isLoading}
                          className="p-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-full hover:from-purple-600 hover:to-pink-600 transition-all duration-200 disabled:opacity-50"
                          title={isPlaying ? "Pause" : "Play"}
                        >
                          {isLoading ? (
                            <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          ) : isPlaying ? (
                            <Pause className="w-6 h-6" />
                          ) : (
                            <PlayArrow className="w-6 h-6" />
                          )}
                        </button>
                        <button
                          onClick={handleTestTTS}
                          disabled={isLoading}
                          className="p-3 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-800/50 transition-all duration-200 disabled:opacity-50"
                          title="Test TTS"
                        >
                          <Refresh className="w-5 h-5" />
                        </button>
                      </div>

                      {/* Progress Bar */}
                      <div 
                        className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full cursor-pointer"
                        onClick={handleSeek}
                      >
                        <div
                          ref={progressRef}
                          className="h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full transition-all duration-200"
                          style={{ width: '0%' }}
                        />
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="space-y-3">
                      <button
                        onClick={handleDownload}
                        disabled={isLoading || !currentText.trim()}
                        className="w-full p-3 bg-gradient-to-r from-green-500 to-teal-500 text-white rounded-lg hover:from-green-600 hover:to-teal-600 transition-all duration-200 disabled:opacity-50 flex items-center justify-center gap-2"
                      >
                        <Download className="w-4 h-4" />
                        Download Audio
                      </button>
                      
                      <button
                        onClick={() => setCurrentText(testContent)}
                        className="w-full p-3 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-lg hover:from-orange-600 hover:to-red-600 transition-all duration-200 flex items-center justify-center gap-2"
                      >
                        <Refresh className="w-4 h-4" />
                        Load Test Content
                      </button>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Audio Element */}
          <audio
            ref={audioRef}
            onEnded={handleAudioEnded}
            onTimeUpdate={handleAudioTimeUpdate}
            className="hidden"
          />
        </div>
      </div>
    </div>
  );
};

export default TTSTest; 